/**
 * Экран AI-торговли: выбор пары и ставки → «скан» → серия Smart Scaling → результат.
 * Визуал по design-spec §7 (AI): Signal Setup, analysis-card, direction-card.
 *
 * Серию считает сервер. Важное отличие от прошлой версии: ответ приходит не
 * целиком, а по частям — `POST /api/trade/cycle` возвращает `runId`, а шаги
 * подтягиваются опросом. Иначе в режиме реальных демо-сделок запрос висел бы
 * всю экспирацию (и обрывался бы по таймауту), а на платформе сделки не
 * появлялись бы вовсе.
 */
import { useCallback, useEffect, useRef, useState } from 'react';
import { SheetPortal } from '../components/SheetPortal.js';
import { AutotradingPanel } from '../components/AutotradingPanel.js';
import { CustomStrategyPanel } from '../components/CustomStrategyPanel.js';
import { ApiError, api, apiMessage } from '../lib/api.js';
import type { AppConfig, Asset, ScanIndicatorGroup, SettingsPayload, TradeRun } from '../lib/api.js';
import { haptic } from '../lib/telegram.js';
import { money, payoutPercent } from '../lib/format.js';
import { useApp } from '../state/app.js';

type Phase = 'setup' | 'analyzing' | 'trading' | 'result';

/** Как часто спрашиваем сервер о состоянии серии. */
const POLL_MS = 1000;
/** Потолок паузы между попытками, когда сервер не отвечает (растёт вдвое за неудачу). */
const POLL_MAX_MS = 15_000;
/** Столько неудач подряд — и опрос сдаётся: экран не должен «идти» вечно. */
const POLL_GIVE_UP = 12;
/** Лимит избранных пар — ровно тот, что принимает сервер (`favouritePairs.maxItems`). */
const MAX_FAVOURITES = 12;

/** Тик анимации «скана»: пары и индикаторы обновляются с этим шагом. */
const SCAN_TICK_MS = 320;
/** Сколько тиков «сканируется» каждый этап, прежде чем получить галочку. */
const TICKS_PER_STAGE = 2;

/** Фолбэк на время загрузки конфига — минимальный пул, чтобы скан не был пустым. */
const FALLBACK_INDICATORS: ScanIndicatorGroup[] = [
  {
    label: 'RSI · MACD',
    metrics: [
      { name: 'RSI', min: 34, max: 80, digits: 1 },
      { name: 'MACD', min: -1.2, max: 1.2, digits: 2 },
    ],
  },
  { label: 'MA50 · MA200', metrics: [{ name: 'MA Δ', min: -0.8, max: 0.8, digits: 2, suffix: '%' }] },
];

/** Случайные 2–3 группы из пула без повторов — свой набор на каждый скан. */
function pickIndicators(pool: ScanIndicatorGroup[]): ScanIndicatorGroup[] {
  const source = pool.length > 0 ? [...pool] : [...FALLBACK_INDICATORS];
  const count = Math.min(source.length, Math.random() < 0.5 ? 2 : 3);
  const picked: ScanIndicatorGroup[] = [];
  while (picked.length < count && source.length > 0) {
    const [group] = source.splice(Math.floor(Math.random() * source.length), 1);
    if (group) picked.push(group);
  }
  return picked;
}

/** Значение метрики для тика: x ∈ [0, 1) разворачивается в диапазон метрики. */
function metricText(metric: ScanIndicatorGroup['metrics'][number], x: number): string {
  const value = metric.min + Math.min(1, Math.max(0, x)) * (metric.max - metric.min);
  return `${metric.name} ${value.toFixed(metric.digits)}${metric.suffix ?? ''}`;
}

/**
 * Живой «скан рынка» вместо статичной карточки: этапы проходят по очереди,
 * тикер перебирает НАСТОЯЩИЕ пары брокера с их payout, растёт счётчик
 * проверенных активов, значения индикаторов «дышат». Числа индикаторов —
 * детерминированные функции тика: живые глазу, стабильные между рендерами.
 * Набор индикаторов случайный на каждый запуск скана (пул — с сервера).
 */
function AnalysisCard({
  assets,
  pool,
  t,
}: {
  assets: Asset[];
  pool: ScanIndicatorGroup[];
  t: (key: string, params?: Record<string, string | number>) => string;
}) {
  const [tick, setTick] = useState(0);
  // набор выбирается один раз на маунт карточки = один прогон скана
  const [indicators] = useState(() => pickIndicators(pool));

  useEffect(() => {
    const timer = setInterval(() => setTick((value) => value + 1), SCAN_TICK_MS);
    return () => clearInterval(timer);
  }, []);

  const stages = [
    t('app.trade.scan_quotes'),
    ...indicators.map((group) => group.label),
    t('app.trade.scan_pattern'),
  ];
  const stageIdx = Math.min(Math.floor(tick / TICKS_PER_STAGE), stages.length);
  const total = assets.length > 0 ? assets.length : 120;
  const progress = Math.min(1, tick / (stages.length * TICKS_PER_STAGE + 2));
  const checked = Math.round(progress * total);
  // простые числа перебираются заметнее, чем подряд идущие индексы
  const current = assets.length > 0 ? assets[(tick * 7) % assets.length] : null;

  const metrics = indicators
    .flatMap((group) => group.metrics)
    .slice(0, 3)
    .map((metric, index) => metricText(metric, ((tick + 1) * 0.731 + index * 0.37) % 1));

  return (
    <section className="card card-lg ai-analysis">
      <h2 className="ai-title ai-analysis-title">{t('app.trade.analyzing')}</h2>

      {/* какую пару сканируем прямо сейчас — настоящие тикеры и payout брокера */}
      <div className="scan-ticker">
        <span className="scan-ticker-pair">{current ? (current.label ?? current.symbol) : '···'}</span>
        {current?.payout !== null && current?.payout !== undefined && (
          <span className="scan-ticker-payout">{payoutPercent(current.payout)}%</span>
        )}
      </div>

      <div className="scan-progress">
        <div className="scan-progress-fill" style={{ width: `${Math.round(progress * 100)}%` }} />
      </div>
      <div className="scan-checked">
        {t('app.trade.scan_checked', { done: checked, total })}
      </div>

      <div className="ai-analysis-rows">
        {stages.map((row, index) => (
          <div
            key={row}
            className={`ai-analysis-row ${index < stageIdx ? 'is-done' : index > stageIdx ? 'is-idle' : ''}`}
          >
            <span>{row}</span>
            {index < stageIdx ? (
              <span className="scan-check">✓</span>
            ) : index === stageIdx ? (
              <span className="ai-shimmer" />
            ) : (
              <span className="scan-wait">·</span>
            )}
          </div>
        ))}
      </div>

      <div className="scan-metrics">
        {metrics.map((metric, index) => (
          <span key={index}>{metric}</span>
        ))}
      </div>

      {stageIdx >= stages.length && <p className="step-hint">{t('app.trade.scan_signal')}</p>}
    </section>
  );
}

/**
 * Шторка выбора пары: весь список активов с payout, звёздочка добавляет
 * пару в любимые (хранятся на сервере) — любимые всегда видны на сетапе.
 */
function PairSheet({
  assets,
  pair,
  favourites,
  onSelect,
  onToggleFavourite,
  onClose,
  t,
}: {
  assets: Asset[];
  pair: string;
  favourites: string[];
  onSelect: (symbol: string) => void;
  onToggleFavourite: (symbol: string) => void;
  onClose: () => void;
  t: (key: string, params?: Record<string, string | number>) => string;
}) {
  // любимые сверху, дальше — по выплате (порядок сервера)
  const ordered = [
    ...assets.filter((asset) => favourites.includes(asset.symbol)),
    ...assets.filter((asset) => !favourites.includes(asset.symbol)),
  ];

  return (
    <SheetPortal>
    <div
      className="sheet-backdrop"
      onClick={(event) => {
        if (event.target === event.currentTarget) onClose();
      }}
    >
      <div className="sheet">
        <div className="sheet-handle" />
        <span className="kicker">{t('app.trade.pair')}</span>
        <h2 className="sheet-title-grad">{t('app.trade.pick_pair')}</h2>
        <p className="body pair-sheet-hint">{t('app.trade.fav_hint')}</p>

        <div className="info-list">
          {ordered.map((asset) => {
            const starred = favourites.includes(asset.symbol);
            return (
              <button
                key={asset.symbol}
                type="button"
                className={`info-row pair-row ${pair === asset.symbol ? 'is-selected' : ''}`}
                onClick={() => {
                  haptic();
                  onSelect(asset.symbol);
                }}
              >
                <span className="grow chip-label">{asset.label ?? asset.symbol}</span>
                {asset.payout !== null && asset.payout !== undefined && (
                  <span className="chip-payout">{payoutPercent(asset.payout)}%</span>
                )}
                <span
                  role="button"
                  tabIndex={0}
                  className={`pair-star ${starred ? 'is-on' : ''}`}
                  onClick={(event) => {
                    event.stopPropagation();
                    haptic();
                    onToggleFavourite(asset.symbol);
                  }}
                  onKeyDown={(event) => {
                    if (event.key === 'Enter' || event.key === ' ') {
                      event.stopPropagation();
                      onToggleFavourite(asset.symbol);
                    }
                  }}
                >
                  {starred ? '⭐' : '☆'}
                </span>
              </button>
            );
          })}
        </div>

        <button
          type="button"
          className="cta cta-secondary cta-block"
          style={{ marginTop: 14 }}
          onClick={onClose}
        >
          {t('app.common.back')}
        </button>
      </div>
    </div>
    </SheetPortal>
  );
}

export function TradeScreen({ config }: { config: AppConfig | null }) {
  const { me, patchMe, showToast, setTab, t } = useApp();
  const [assets, setAssets] = useState<Asset[]>([]);
  const [settings, setSettings] = useState<SettingsPayload | null>(null);
  const [pair, setPair] = useState('');
  const [amount, setAmount] = useState(5);
  const [phase, setPhase] = useState<Phase>('setup');
  const [run, setRun] = useState<TradeRun | null>(null);
  const [starting, setStarting] = useState(false);
  const [pairSheetOpen, setPairSheetOpen] = useState(false);

  /** Идентификатор серии, за которой следим; null — следить не за чем. */
  const watching = useRef<string | null>(null);

  useEffect(() => {
    void api
      .assets()
      .then(({ assets: list }) => {
        setAssets(list);
        if (list[0]) setPair((current) => current || list[0]!.symbol);
      })
      .catch(() => undefined);
    void api.settings().then(setSettings).catch(() => undefined);
  }, []);

  /** Применяем состояние серии к экрану: шаги, фаза, баланс и токены. */
  const apply = useCallback(
    (next: TradeRun) => {
      // ответ незнакомой формы — почти наверняка старая страница из кэша
      // Telegram против обновлённого API. Рисовать по нему нельзя: получится
      // пустая серия с нулевым результатом (так и вышло 2026-08-20)
      if (!next || typeof next.status !== 'string' || !Array.isArray(next.steps)) {
        setPhase('setup');
        showToast(t('app.trade.outdated'));
        return;
      }

      setRun(next);

      if (next.status === 'running') {
        // «анализ» держим только до первой сделки — дальше показываем шаги
        setPhase(next.steps.length > 0 || next.pending ? 'trading' : 'analyzing');
        return;
      }

      watching.current = null;
      setPhase(next.status === 'failed' && next.steps.length === 0 ? 'setup' : 'result');

      if (next.status === 'failed' && next.steps.length === 0) {
        showToast(t(next.errorKey ?? 'app.trade.failed'));
      }

      if (me) {
        patchMe({
          user: { ...me.user, tokens: next.tokensLeft },
          balances: { ...me.balances, demo: next.balance },
        });
      }
    },
    [me, patchMe, showToast, t],
  );

  /**
   * Опрос состояния серии — пока она идёт.
   *
   * Ошибки не «глотаются» до бесконечности (разбор 2026-09-10: экран с
   * протухшей подписью дёргал сервер раз в секунду часами — 1 778 × 401 за
   * час): 401/404 останавливают опрос сразу, сетевые сбои — с растущей
   * паузой и потолком попыток.
   */
  const pollFailures = useRef(0);
  useEffect(() => {
    if (!run || run.status !== 'running') {
      pollFailures.current = 0;
      return;
    }
    watching.current = run.runId;

    let cancelled = false;
    const delay = Math.min(POLL_MS * 2 ** pollFailures.current, POLL_MAX_MS);
    const timer = globalThis.setTimeout(() => {
      void api
        .tradeRun(run.runId)
        .then((next) => {
          pollFailures.current = 0;
          if (!cancelled && watching.current === next.runId) apply(next);
        })
        .catch((err: unknown) => {
          if (cancelled) return;
          const status = err instanceof ApiError ? err.status : 0;
          // подпись протухла — дальше все запросы бессмысленны, экран входа
          // покажет AppProvider; серии на сервере нет — она не «идёт»
          const fatal = status === 401 || status === 404;
          pollFailures.current += 1;
          if (fatal || pollFailures.current >= POLL_GIVE_UP) {
            watching.current = null;
            pollFailures.current = 0;
            setRun(null);
            setPhase('setup');
            if (status !== 401) showToast(t(status === 404 ? 'app.trade.failed' : 'app.error.load_failed'));
            return;
          }
          // разрыв связи не должен ронять экран: следующий тик попробует снова
          setRun((current) => (current ? { ...current } : current));
        });
    }, delay);

    return () => {
      cancelled = true;
      globalThis.clearTimeout(timer);
    };
  }, [run, apply, showToast, t]);

  /** Приложение открыли на середине серии — подхватываем её, а не теряем. */
  useEffect(() => {
    void api
      .activeRun()
      .then(({ run: active }) => {
        if (active) apply(active);
      })
      .catch(() => undefined);
    // осознанно один раз при монтировании: дальше состояние ведёт опрос
  }, []);

  async function launch(session = false) {
    if (starting) return;
    haptic('medium');
    setStarting(true);
    setPhase('analyzing');
    setRun(null);

    try {
      const started = await api.runCycle({
        amount,
        ...(pair ? { pair } : {}),
        ...(session ? { session: true } : {}),
      });
      apply(started);
    } catch (err) {
      setPhase('setup');
      if (err instanceof ApiError && err.isOutOfTokens) {
        showToast(t('app.trade.no_tokens'));
        setTab('tokens');
        return;
      }
      // аккаунт не привязан — говорим на языке пользователя и ведём к привязке
      const code =
        err instanceof ApiError ? (err.payload as { error?: string } | null)?.error : null;
      showToast(apiMessage(err, t, 'app.trade.failed'));
      if (code === 'account_required') setTab('account');
    } finally {
      setStarting(false);
    }
  }

  const bets = settings?.options.bets ?? [
    { amount: 1 },
    { amount: 3 },
    { amount: 5 },
    { amount: 10, risk: true },
    { amount: 20, risk: true },
  ];
  const strategy = settings?.current;
  const favourites = settings?.current.favouritePairs ?? [];

  /** Любимые пары переключаются звёздочкой в шторке; храним на сервере. */
  const toggleFavourite = (symbol: string): void => {
    const starred = favourites.includes(symbol);
    // лимит тот же, что у сервера: раньше 13-я звезда ставилась локально, сервер
    // отвечал 400, и дальше не сохранялось уже ничего (56 отказов за 4 дня, 2026-09-10)
    if (!starred && favourites.length >= MAX_FAVOURITES) {
      showToast(t('app.trade.favourites_limit', { max: MAX_FAVOURITES }));
      return;
    }
    const previous = favourites;
    const next = starred ? favourites.filter((item) => item !== symbol) : [...favourites, symbol];
    const withFavourites = (list: string[]) => (current: SettingsPayload | null) =>
      current ? { ...current, current: { ...current.current, favouritePairs: list } } : current;
    // оптимистично: экран отвечает мгновенно, сервер догоняет; не принял — откатываем,
    // иначе локальный список расходится с сохранённым
    setSettings(withFavourites(next));
    api.updateSettings({ favouritePairs: next }).catch(() => {
      setSettings(withFavourites(previous));
      showToast(t('app.account.save_failed'));
    });
  };

  const allPairs =
    assets.length > 0 ? assets : [{ symbol: 'EURUSD_otc', label: 'EUR/USD OTC' } as Asset];
  // на сетапе — любимые пары (+ выбранная); пока любимых нет — топ-4 по выплате
  const favouriteAssets = allPairs.filter((asset) => favourites.includes(asset.symbol));
  const shownPairs = [...(favouriteAssets.length > 0 ? favouriteAssets : allPairs.slice(0, 4))];
  if (pair && !shownPairs.some((asset) => asset.symbol === pair)) {
    const selected = allPairs.find((asset) => asset.symbol === pair);
    if (selected) shownPairs.unshift(selected);
  }

  // «Автотрейдинг» (стратегия по таймеру) — свой экран вместо сетапа серии:
  // ставка в % от баланса, сессия идёт на сервере
  const activePreset = settings?.options.strategies.find((p) => p.id === settings.current.strategy);
  // «Своя стратегия»: именованные пресеты с ползунками, сессия — как у автотрейдинга
  if (settings && activePreset?.custom) {
    return (
      <CustomStrategyPanel
        settings={settings}
        onSettings={setSettings}
        brandName={config?.brand.name ?? ''}
      />
    );
  }
  if (settings && activePreset?.timed) {
    return (
      <AutotradingPanel
        settings={settings}
        onSettings={setSettings}
        brandName={config?.brand.name ?? ''}
      />
    );
  }

  return (
    <div className="screen">
      {phase === 'setup' && (
        <>
          <section className="card card-lg">
            <div className="ai-head">
              <img className="ai-emblem" src="/icons/trade.svg" alt="" width={64} height={64} />
              <div>
                <span className="kicker">{config?.brand.name ? `${config.brand.name} AI` : 'AI'}</span>
                <h2 className="ai-title">{t('app.trade.setup_title')}</h2>
              </div>
            </div>

            <div className="stack">
              <div>
                <div className="label">{t('app.trade.pair')}</div>
                {/* любимые пары (звёздочка в шторке) + выбранная; остальное — в шторке */}
                <div className="chip-grid">
                  {shownPairs.map((asset) => (
                    <button
                      key={asset.symbol}
                      type="button"
                      className={`chip ${pair === asset.symbol ? 'is-active' : ''}`}
                      onClick={() => {
                        haptic();
                        setPair(asset.symbol);
                      }}
                    >
                      <span className="chip-label">
                        {favourites.includes(asset.symbol) ? '⭐ ' : ''}
                        {asset.label ?? asset.symbol}
                      </span>
                      {asset.payout !== null && asset.payout !== undefined && (
                        <span className="chip-payout">{payoutPercent(asset.payout)}%</span>
                      )}
                    </button>
                  ))}
                  <button
                    type="button"
                    className="chip chip-more"
                    onClick={() => {
                      haptic();
                      setPairSheetOpen(true);
                    }}
                  >
                    <span className="chip-label">{t('app.trade.all_pairs')}</span>
                  </button>
                </div>
              </div>

              <div>
                <div className="label">{t('app.trade.amount')}</div>
                <div className="chip-grid" style={{ '--chip-cols': 3 } as React.CSSProperties}>
                  {bets.map((bet) => (
                    <button
                      key={bet.amount}
                      type="button"
                      className={`chip ${amount === bet.amount ? 'is-active' : ''}`}
                      onClick={() => {
                        haptic();
                        setAmount(bet.amount);
                      }}
                    >
                      ${bet.amount}
                      {bet.risk ? ' RISK' : ''}
                    </button>
                  ))}
                </div>
              </div>
            </div>

            {strategy && (
              <div className="ai-strategy-note">
                {t('app.trade.strategy_line', {
                  strategy:
                    settings?.options.strategies.find((p) => p.id === strategy.strategy)?.label ??
                    strategy.strategy,
                  progression: strategy.progression,
                  maxSteps: strategy.maxSteps,
                  expiration: strategy.expirationSeconds,
                })}
              </div>
            )}

            <button
              type="button"
              className="cta cta-launch cta-block"
              disabled={starting}
              onClick={() => void launch()}
            >
              {t('app.trade.generate')}
            </button>

            {/* сессия: maxTrades серий подряд одним запуском (токены — за
                каждую серию); кнопка вторична, чтобы не спорить с главной */}
            {(strategy?.maxTrades ?? 0) > 1 && (
              <button
                type="button"
                className="cta cta-secondary cta-block"
                disabled={starting}
                onClick={() => void launch(true)}
              >
                {t('app.trade.session_button', { count: strategy?.maxTrades ?? 0 })}
              </button>
            )}
          </section>

          <section className="card">
            <div className="row-between">
              <span className="label">{t('app.trade.tokens_available')}</span>
              <span className="pill">{me?.user.tokens ?? 0} 💎</span>
            </div>
            <p className="body">{t('app.trade.cycle_note')}</p>
          </section>
        </>
      )}

      {phase === 'analyzing' && (
        <AnalysisCard assets={assets} pool={config?.scanIndicators ?? []} t={t} />
      )}

      {pairSheetOpen && (
        <PairSheet
          assets={allPairs}
          pair={pair}
          favourites={favourites}
          onSelect={(symbol) => {
            setPair(symbol);
            setPairSheetOpen(false);
          }}
          onToggleFavourite={toggleFavourite}
          onClose={() => setPairSheetOpen(false)}
          t={t}
        />
      )}

      {(phase === 'trading' || phase === 'result') && run && (
        <>
          <section className={`card card-lg direction-card ${run.profit >= 0 ? 'is-up' : 'is-down'}`}>
            <span className="kicker">{run.pairLabel ?? run.pair}</span>
            <div className="value-xl">{run.action === 'call' ? 'LONG' : 'SHORT'}</div>
            <span className="pill pill-green">{t('app.trade.smart_scaling')}</span>
            {/* видно, настоящие это сделки или превью — иначе не отличить;
                реальный счёт помечается отдельно: там настоящие деньги */}
            <span className={`pill ${run.market === 'real' ? 'pill-green' : ''}`}>
              {t(
                run.market === 'real'
                  ? 'app.trade.real_badge'
                  : run.live
                    ? 'app.trade.live_badge'
                    : 'app.trade.demo_badge',
              )}
            </span>
          </section>

          <section className="card">
            <div className="steps-list">
              {run.steps.map((step) => (
                <div key={step.index} className={`step-row ${step.won ? 'is-win' : 'is-loss'}`}>
                  <span className="step-index">
                    {/* в корзине (basket) каждый шаг на своей паре — она важнее номера */}
                    {step.pairLabel ?? step.pair ?? t('app.trade.step', { index: step.index })}
                  </span>
                  <span className="step-amount">${money(step.amount)}</span>
                  <span className="step-profit">
                    {step.profit > 0 ? '+' : '−'}${money(Math.abs(step.profit))}
                  </span>
                </div>
              ))}

              {/* открытая сделка: у брокера она живёт всю экспирацию, и без
                  этой строки экран выглядел бы зависшим */}
              {run.pending && (
                <div className="step-row is-pending">
                  <span className="step-index">
                    {t('app.trade.step', { index: run.pending.index })}
                  </span>
                  <span className="step-amount">${money(run.pending.amount)}</span>
                  <span className="step-profit">
                    <span className="ai-shimmer" />
                  </span>
                </div>
              )}
            </div>

            {run.status === 'running' && (
              <p className="step-hint">{t('app.trade.waiting')}</p>
            )}
          </section>
        </>
      )}

      {phase === 'result' && run && (
        <section className="card card-lg victory-card">
          <h2 className="card-title">
            {run.status === 'failed'
              ? t(run.errorKey ?? 'app.trade.failed')
              : t(run.won ? 'app.trade.win_title' : 'app.trade.loss_title')}
          </h2>
          <div className="victory-grid">
            <div>
              <div className="label">{t('app.trade.result')}</div>
              <div className={`victory-value ${run.profit >= 0 ? 'is-win' : 'is-loss'}`}>
                {run.profit >= 0 ? '+' : '−'}${money(Math.abs(run.profit))}
              </div>
            </div>
            <div>
              <div className="label">{t('app.trade.balance')}</div>
              <div className="victory-value">${money(run.balance)}</div>
            </div>
            <div>
              <div className="label">{t('app.trade.tokens_spent')}</div>
              <div className="victory-value">{run.tokensSpent} 💎</div>
            </div>
          </div>

          <div className="row">
            <button
              type="button"
              className="cta cta-launch grow"
              onClick={() => {
                setPhase('setup');
                setRun(null);
              }}
            >
              {t('app.trade.again')}
            </button>
            <button type="button" className="cta cta-secondary grow" onClick={() => setTab('home')}>
              {t('app.trade.to_menu')}
            </button>
          </div>
        </section>
      )}
    </div>
  );
}
